import React from "react";
import CaseStudy from "../../components/caseStudy/CaseStudy";
import CodeQueryApp from "../../assets/codequery.png";
import CodeQuerySearch from "../../assets/codequery-search.png";
import CodeQueryAsk from "../../assets/codequery-ask.png";
import CodeQueryPost from "../../assets/codequery-post.png";
import CodeQueryAnswers from "../../assets/codequery-answers.png";
import CodeQueryRelated from "../../assets/codequery-related.png";
import { FaReact, FaNodeJs, FaGitAlt } from "react-icons/fa";
import { SiExpress, SiPostgresql, SiPython, SiFastapi, SiHuggingface, SiPytorch, SiNumpy } from "react-icons/si";
import { MdAutoAwesome } from "react-icons/md";

// Content only — the layout lives in components/caseStudy/CaseStudy.jsx.
const study = {
  slug: "codequery",
  title: "CodeQuery",
  subtitle:
    "A Stack Overflow-style community platform where questions are found by meaning rather than keywords, and a local language model writes answers grounded in — and cited from — the community's own discussions.",
  badges: [
    { label: "AI-Powered Q&A Platform" },
    { label: "Semantic search + RAG", icon: <MdAutoAwesome size={13} /> },
  ],
  tags: ["React", "Node.js", "PostgreSQL", "Python", "Sentence Transformers", "HuggingFace Transformers", "RAG"],
  facts: [
    { label: "Type", value: "Developer Q&A community platform" },
    { label: "AI", value: "Semantic search and retrieval-augmented answers, with open models running locally" },
    { label: "Architecture", value: "React SPA + Express REST API + Python FastAPI service + PostgreSQL" },
    { label: "Models", value: "all-MiniLM-L6-v2 (embeddings) · Qwen2.5-0.5B-Instruct (generation)" },
  ],
  demoUrl: "https://app.arcade.software/share/aMCghWN3Sb7fysoexdXA",
  media: [
    { type: "image", src: CodeQueryAsk, label: "Ask AI", alt: "CodeQuery search results with an AI answer that cites the discussion it was generated from" },
    { type: "image", src: CodeQueryApp, label: "Question feed", alt: "CodeQuery home feed listing the latest posts with tags, votes, answers and views" },
  ],

  overview: {
    title: "What is CodeQuery?",
    paragraphs: [
      "CodeQuery is a community Q&A platform for developers: people post questions and write-ups, others answer, vote and comment, and the author accepts the answer that solved the problem.",
      "On top of that sits an AI layer built with open-source models that run locally. Every post and answer is embedded, so the search box understands what a question means, each post suggests related discussions, and an \"Ask AI\" button generates an answer from the most relevant community content — and shows which discussions it used.",
    ],
  },
  problem: {
    paragraphs: [
      "Keyword search fails developers in a specific way: the person asking rarely uses the words of the person who answered. \"My effect fires two times\" and \"Why does useEffect run twice in React 18?\" are the same question, and a substring match will never connect them — so the same questions get asked again.",
      "Plugging in a general-purpose chatbot does not solve it either: it answers from its own memory, cannot point to a source, and ignores everything the community has already written. The goal was an assistant that only speaks from the platform's own content, and says so when that content has nothing to offer.",
    ],
  },
  solution: {
    steps: [
      "React client — search, Ask AI, answers",
      "Express REST API — auth, posts, votes",
      "Python FastAPI service — embeddings + generation",
      "Sentence Transformers · HuggingFace Transformers",
      "PostgreSQL — relational data + embedded chunks",
    ],
    note:
      "The browser only ever talks to the Node API. Node owns the relational data and delegates anything AI-related to the Python service, which reads posts from the same PostgreSQL database and writes their embeddings back to it.",
  },

  features: [
    {
      title: "Semantic Search",
      description: "The query is embedded with a Sentence Transformers model and compared with every indexed passage by cosine similarity. Results carry a match percentage and the passage that matched.",
    },
    {
      title: "Hybrid Matching",
      description: "Meaning-based results come first, followed by plain text matches on title, summary and tags — so a partial word or a single tag still finds something.",
    },
    {
      title: "Ask AI (RAG)",
      description: "A question retrieves the top passages from posts and answers, and a HuggingFace Transformers model writes a short answer from them. The sources are listed under the answer with their relevance.",
    },
    {
      title: "AI Answer per Question",
      description: "Any question page can generate an AI answer from related discussions. It is cached on the post, and cleared when the post is edited.",
    },
    {
      title: "Answers, Votes & Accepted Answer",
      description: "Answers are ranked by score, each user has one vote per answer, and the question's author can mark one answer as accepted — it is pinned to the top.",
    },
    {
      title: "Related Questions",
      description: "Each post lists the discussions closest to it in embedding space, computed from the average of its passages.",
    },
    {
      title: "Rich Posts",
      description: "Posts are written in a rich-text editor with code blocks, tags and an optional cover image, and collect likes, views and comments.",
    },
    {
      title: "Graceful Degradation",
      description: "If the AI service is down, search falls back to SQL text matching and the rest of the platform keeps working; posting never waits on the index.",
    },
    {
      title: "Authentication",
      description: "Registration and login with bcrypt-hashed passwords and a JWT cookie; editing and deleting are restricted to the author on the server.",
    },
  ],

  gallery: [
    { src: CodeQuerySearch, caption: "Semantic search — a question phrased in the user's own words finds the matching discussion, with a match score and the passage that matched." },
    { src: CodeQueryAsk, caption: "Ask AI — an answer generated from the retrieved passages, followed by the discussion it is based on and its relevance." },
    { src: CodeQueryPost, caption: "Question page — the post with its tags, author, votes and view count." },
    { src: CodeQueryAnswers, caption: "AI answer and community answers — the cached AI answer with its source, then answers ranked by votes with the accepted one first." },
    { src: CodeQueryRelated, caption: "Related questions — the closest discussions by embedding similarity, at the bottom of every post." },
    { src: CodeQueryApp, caption: "Feed — the latest posts with tags, likes, answer count and views." },
  ],
  galleryNote: "Screenshots use sample content. Select one to enlarge it.",

  diagrams: [
    {
      label: "Indexing a post",
      steps: [
        "Post or answer created, edited or deleted",
        "Node API saves it, then notifies the AI service",
        "HTML stripped, text split into overlapping chunks",
        "Each chunk embedded (384-dimension vector)",
        "Chunks + vectors stored in PostgreSQL",
      ],
    },
    {
      label: "Answering a question (RAG)",
      steps: [
        "Question embedded with the same model",
        "Cosine similarity against every chunk",
        "Chunks under the relevance threshold dropped",
        "Top chunks passed to the generator as numbered excerpts",
        "Answer returned with its sources",
      ],
    },
  ],

  challenges: [
    {
      title: "Search by meaning without losing exact matches",
      problem: "Embeddings connect differently worded questions, but a single letter, a stop word or half a word carries no meaning to embed — a purely semantic search returned nothing for them.",
      solution: "Search is hybrid: semantic results above a similarity threshold are ranked first, then text matches on title, summary and tags that the embeddings missed are appended. If the AI service is unreachable, the text matches alone are returned.",
    },
    {
      title: "An assistant that does not answer from thin air",
      problem: "A language model will happily answer any question, including ones the community has never discussed — which is exactly what a grounded assistant must not do.",
      solution: "Retrieval decides first. If no passage clears the relevance threshold, no text is generated and the user is told the community has not covered the topic yet. Otherwise the prompt restricts the model to the numbered excerpts, and the answer is always shown with its sources and a reminder to verify it.",
    },
    {
      title: "Long posts full of markup and code",
      problem: "Posts are stored as rich-text HTML and can be much longer than what an embedding model reads well; embedding a whole post blurs it into one vague vector.",
      solution: "The HTML is reduced to text, then split on line boundaries into overlapping windows of about 160 words. Every chunk is prefixed with the post's title so it stays self-describing, and a post is ranked by its best chunk rather than an average.",
    },
    {
      title: "Keeping the index in step with the content",
      problem: "An answer added to a question should be searchable immediately, and a deleted post must stop appearing — without making every write wait on a model.",
      solution: "After each write the API asks the AI service to rebuild that one post's chunks, without blocking the response; chunks are tied to posts and answers by foreign keys, so deletions cascade in the database. A full re-index is one endpoint call.",
    },
    {
      title: "Two runtimes, one source of truth",
      problem: "The API is Node.js while the NLP ecosystem is Python; duplicating data between the two would mean two things to keep consistent.",
      solution: "Both services share one PostgreSQL database with a clear split: Node owns users, posts, answers and votes, and the Python service only reads them and owns the chunk table. Vectors are stored as plain float arrays, so no database extension is required.",
    },
    {
      title: "Running generation on modest hardware",
      problem: "The models run locally on a CPU, where a large model is too slow to use and a small one is more easily led astray.",
      solution: "The default generator is a 0.5B-parameter instruct model with a short answer budget, a tight prompt and only the top three excerpts; per-question answers are cached. Both models are configuration values, so a larger one can be swapped in where the hardware allows.",
    },
  ],

  stack: [
    { group: "Frontend", items: ["React 18", "React Router", "Axios", "React Quill", "date-fns"] },
    { group: "API", items: ["Node.js", "Express", "REST API", "Multer", "Joi validation"] },
    { group: "AI service", items: ["Python", "FastAPI", "Sentence Transformers", "HuggingFace Transformers", "PyTorch", "NumPy"] },
    { group: "Models", items: ["all-MiniLM-L6-v2 (embeddings)", "Qwen2.5-0.5B-Instruct (generation)"] },
    { group: "Database", items: ["PostgreSQL", "node-postgres", "psycopg"] },
    { group: "Security", items: ["JWT", "bcrypt password hashing", "Server-side ownership checks"] },
  ],
  stackIcons: [
    [FaReact, "React"],
    [FaNodeJs, "Node.js"],
    [SiExpress, "Express"],
    [SiPostgresql, "PostgreSQL"],
    [SiPython, "Python"],
    [SiFastapi, "FastAPI"],
    [SiHuggingface, "HuggingFace"],
    [SiPytorch, "PyTorch"],
    [SiNumpy, "NumPy"],
    [FaGitAlt, "Git"],
  ],
};

const CodeQueryCaseStudy = () => <CaseStudy study={study} />;

export default CodeQueryCaseStudy;
